import moment from 'moment'

export const TOP_EVENTS_DEFAULT = 5

export const ROLLING_RANGE_OPTIONS = [
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
]

/** Net revenue for one sales-per-game row. */
export function eventNetRevenue(row) {
  const n = Number(row?.netWithPackages ?? row?.net ?? 0)
  return Number.isFinite(n) ? n : 0
}

export function pctDelta(current, prior) {
  if (current == null || prior == null) return null
  const c = Number(current) || 0
  const p = Number(prior) || 0
  if (p === 0 && c === 0) return null
  if (p === 0) return null
  return Math.round(((c - p) / p) * 100)
}

/** Human-readable delta line; avoids fake 100% when prior period was zero. */
export function formatDeltaLine(delta, suffix = 'vs prior period') {
  if (delta == null) {
    return { text: `No prior period data`, className: '' }
  }
  const dir = delta >= 0 ? '↑' : '↓'
  return {
    text: `${dir} ${Math.abs(delta)}% ${suffix}`,
    className:
      delta >= 0
        ? 'reporting-overview__metric-delta--up'
        : 'reporting-overview__metric-delta--down',
  }
}

export function aggregateSalesPerGameRows(rows) {
  const list = Array.isArray(rows) ? rows : []
  let revenue = 0
  let ticketsSold = 0
  let comp = 0
  let scanned = 0
  let unscanned = 0

  for (const r of list) {
    revenue += eventNetRevenue(r)
    ticketsSold += Number(r.soldQuantity || 0)
    comp += Number(r.compQuantity || 0)
    scanned += Number(r.scanned || 0)
    unscanned += Number(r.unscanned || 0)
  }

  const distributed = scanned + unscanned
  const attendancePct =
    distributed > 0 ? Math.round((scanned / distributed) * 100) : null
  const aov = ticketsSold > 0 ? revenue / ticketsSold : null

  return {
    revenue,
    ticketsSold,
    comp,
    scanned,
    unscanned,
    distributed,
    attendancePct,
    aov,
  }
}

export function revenueByEventFromRows(rows, limit = TOP_EVENTS_DEFAULT) {
  const list = Array.isArray(rows) ? rows : []
  const sorted = [...list].sort(
    (a, b) => eventNetRevenue(b) - eventNetRevenue(a)
  )
  return sorted.slice(0, limit).map((r) => ({
    name: r.eventName || 'Event',
    amount: eventNetRevenue(r),
  }))
}

export function paidVsCompBars(agg) {
  return [
    { name: 'Paid tickets sold', count: Math.round(agg.ticketsSold) },
    { name: 'Complimentary', count: Math.round(agg.comp) },
  ]
}

export function barsWithPct(rows, valueKey) {
  if (!rows.length) return []
  const max = Math.max(...rows.map((r) => r[valueKey]), 1)
  return rows.map((r) => ({
    ...r,
    pct: Math.round((r[valueKey] / max) * 100),
  }))
}

export function snapshotByEventName(salesAttendanceRows) {
  const map = new Map()
  for (const row of salesAttendanceRows || []) {
    if (row?.isMonthlyTotal) continue
    const key = String(row.eventName || '')
      .trim()
      .toLowerCase()
    if (!key) continue
    map.set(key, {
      sold: Number(row.sold || 0),
      total: Number(row.total || 0),
    })
  }
  return map
}

export function getRollingRanges(rangeId) {
  const opt = ROLLING_RANGE_OPTIONS.find((o) => o.id === rangeId)
  const n = opt?.days ?? 30
  const currentEnd = moment().endOf('day')
  const currentStart = moment().subtract(n, 'days').startOf('day')
  const priorEnd = moment(currentStart).subtract(1, 'millisecond')
  const priorStart = moment(priorEnd).clone().subtract(n, 'days').startOf('day')
  return {
    currentStart,
    currentEnd,
    priorStart,
    priorEnd,
    rangeLabel: opt?.label ?? 'Last 30 days',
  }
}

/** Season start through today (or season end if earlier). */
export function getSeasonToDateRanges(season) {
  if (!season?.start) return null
  const currentStart = moment(season.start).startOf('day')
  const seasonEnd = season.ends
    ? moment(season.ends).endOf('day')
    : moment().endOf('day')
  const currentEnd = moment.min(moment().endOf('day'), seasonEnd)
  if (currentEnd.isBefore(currentStart)) {
    return null
  }
  const dayCount = Math.max(currentEnd.diff(currentStart, 'days'), 1)
  const priorEnd = moment(currentStart).subtract(1, 'millisecond')
  const priorStart = moment(priorEnd)
    .clone()
    .subtract(dayCount, 'days')
    .startOf('day')
  const name = season.name || 'Season'
  return {
    currentStart,
    currentEnd,
    priorStart,
    priorEnd,
    rangeLabel: `${name} (season to date)`,
  }
}

export function resolvePeriodRanges(rangeId, season) {
  if (rangeId === 'season') {
    const ranges = getSeasonToDateRanges(season)
    if (ranges) return ranges
  }
  return getRollingRanges(rangeId)
}

export function mapEventSaleStatus(status) {
  const s = String(status || '').toLowerCase()
  if (s === 'on_sale') {
    return { label: 'On sale', variant: 'success' }
  }
  if (s === 'complete') {
    return { label: 'Sale ended', variant: 'secondary' }
  }
  if (s === 'unpublished' || s === 'draft' || s === 'scheduled') {
    return { label: 'Not on sale', variant: 'secondary' }
  }
  if (!s) {
    return { label: 'Unknown', variant: 'secondary' }
  }
  return { label: s.replace(/_/g, ' '), variant: 'warning' }
}

/**
 * @param {object} ev - org event with optional capacity, status
 * @param {{ sold: number, total: number }|undefined} snapshot
 */
export function buildUpcomingEventRow(ev, snapshot) {
  const soldPaid = snapshot?.sold ?? 0
  const capacityNum = Number(ev?.capacity)
  const hasCapacity = Number.isFinite(capacityNum) && capacityNum > 0
  const sold = hasCapacity || soldPaid > 0 ? soldPaid : null
  const capacity = hasCapacity ? capacityNum : null
  const fillRatePct =
    hasCapacity && sold != null
      ? Math.min(100, Math.round((sold / capacityNum) * 100))
      : null

  const { label, variant } = mapEventSaleStatus(ev?.status)

  return {
    key: ev.uuid || `${ev.name}-${ev.start}`,
    title: ev.name,
    when: ev.when,
    venue: ev.venueName || null,
    sold,
    capacity,
    fillRatePct,
    capacityUnavailable: !hasCapacity,
    status: label,
    statusVariant: variant,
  }
}

export const TOP_ATTENDANCE_EVENTS = 8
export const TOP_SECTIONS_ATTENTION = 8
export const MIN_SECTION_TICKETS = 5

/** Per-event scan metrics from sales-per-game rows (period-scoped). */
export function eventAttendanceFromSalesPerGame(
  rows,
  limit = TOP_ATTENDANCE_EVENTS
) {
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => r?.eventName)
    .map((r) => {
      const scanned = Number(r.scanned || 0)
      const unscanned = Number(r.unscanned || 0)
      const distributed = scanned + unscanned
      const rate =
        distributed > 0 ? Math.round((scanned / distributed) * 100) : null
      return {
        eventName: r.eventName,
        eventDate: r.eventDate || '',
        scanned,
        unscanned,
        distributed,
        rate,
        paid: Number(r.soldQuantity || 0),
        comp: Number(r.compQuantity || 0),
      }
    })
    .filter((r) => r.distributed > 0)
    .sort((a, b) => (a.rate ?? 100) - (b.rate ?? 100))
    .slice(0, limit)
}

/** Roll up sales-channel report rows by channel (across events). */
export function aggregateSalesChannelRows(rows) {
  const byChannel = new Map()
  for (const r of rows || []) {
    const channel = r.salesChannel || 'Unknown'
    if (!byChannel.has(channel)) {
      byChannel.set(channel, {
        channel,
        quantity: 0,
        grossSales: 0,
        netSales: 0,
      })
    }
    const agg = byChannel.get(channel)
    agg.quantity += Number(r.quantity || 0)
    agg.grossSales += Number(r.grossSales || 0)
    agg.netSales += Number(r.netSales || 0)
  }
  return [...byChannel.values()]
    .filter((c) => c.quantity > 0 || c.netSales > 0)
    .sort((a, b) => b.netSales - a.netSales)
}

/** Sections with lowest scan rates (for ops focus). */
export function sectionsNeedingAttention(
  rows,
  { minQuantity = MIN_SECTION_TICKETS, limit = TOP_SECTIONS_ATTENTION } = {}
) {
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => r?.section && Number(r.quantity) >= minQuantity)
    .map((r) => {
      const qty = Number(r.quantity || 0)
      const scanned = Number(r.scanned || 0)
      const rawRate = r.scanrate ?? r.scanRate
      const scanRate =
        rawRate != null && rawRate !== ''
          ? Math.round(Number(rawRate))
          : qty > 0
            ? Math.round((scanned / qty) * 100)
            : 0
      return {
        eventName: r.eventName || '—',
        section: r.section,
        quantity: qty,
        scanned,
        unscanned: Number(r.unscanned ?? qty - scanned),
        scanRate,
      }
    })
    .sort((a, b) => a.scanRate - b.scanRate)
    .slice(0, limit)
}

export const ATTENDANCE_REPORT_LINKS = [
  {
    path: '/access-control-summary',
    title: 'Access Control Summary',
    description: 'Ticket distribution and scan totals by event.',
  },
  {
    path: '/sales-attendance',
    title: 'Event Sales & Attendance',
    description: 'Full season view of sold, comp, scanned, and proceeds.',
  },
  {
    path: '/attendance-summary',
    title: 'Attendance Summary',
    description: 'Per-event attendance breakdown by sales channel.',
  },
]

export const SALES_REPORT_LINKS = [
  {
    path: '/order-listings',
    title: 'Order Listings',
    description: 'Order-level gross, net, fees, and payment detail.',
  },
  {
    path: '/sales-attendance',
    title: 'Event Sales & Attendance',
    description: 'Ticket proceeds and sales by event.',
  },
  {
    path: '/payment-method-type-report',
    title: 'Payment Method Report',
    description: 'Sales grouped by payment method type.',
  },
]

export const FINANCE_REPORT_LINKS = [
  {
    path: '/order-listings',
    title: 'Order Listings',
    description: 'Payment listings, fees, and order-level financial detail.',
  },
  {
    path: '/deferred-revenue',
    title: 'Season Deferred Revenue',
    description:
      'Unearned season ticket revenue and month-end recognition rollforward.',
  },
  {
    path: '/flex-pack-deferred-revenue',
    title: 'Flex Pack Deferred Revenue',
    description:
      'Unearned flex pack voucher revenue and month-end redemption/breakage rollforward.',
  },
  {
    path: '/ar-aging-summary',
    title: 'AR Aging Summary',
    description: 'Accounts receivable aging summary.',
  },
  {
    path: '/ar-aging-details',
    title: 'AR Aging Details',
    description: 'Accounts receivable aging detail.',
  },
  {
    path: '/sales-receivables-by-category',
    title: 'Sales & Receivables by Category',
    description:
      'Total sales by category less payments collected equals outstanding receivables, with flex pack price reconciliation.',
  },
  {
    path: '/buyer-types-report',
    title: 'Buyer Types Report',
    description:
      'Orders, sales, collections, and outstanding balances grouped by buyer type.',
  },
  {
    path: '/daily-settlement',
    title: 'Daily Settlement',
    description: 'Daily settlement and payout reconciliation.',
  },
  {
    path: '/aged-receivables',
    title: 'Aged Receivables',
    description: 'Outstanding receivables by age bucket.',
  },
]
